export const MASQUERADE_COMPANY_ID_KEY = 'masqueradeCompanyId'

/**
 * Dispatched on `window` when this tab starts, switches, or stops masquerading.
 *
 * Same-tab only — this is not the cross-tab `storage` event, and deliberately so. `AuthorizeUser`
 * listens for it so a switch takes effect immediately instead of waiting for a route change: the
 * corporate location switcher and the "Back to Corporate Dashboard" button both navigate to `/`, so
 * on any code path that was already at `/` the switch used to silently do nothing until a manual
 * reload.
 */
export const MASQUERADE_CHANGED_EVENT = 'ps:masquerade-changed'

function announceChange(): void {
  try {
    window?.dispatchEvent?.(new Event(MASQUERADE_CHANGED_EVENT))
  } catch {
    // no window (SSR/tests without a DOM) — callers still get the stored value
  }
}

/**
 * Masquerade context is **per tab**, not per origin.
 *
 * It used to live in `localStorage`, which is shared by every tab on the origin. That single shared
 * slot was the root of most masquerade instability: opening a second masquerade window silently
 * re-pointed the first one, "Stop Masquerading" kicked every other tab out mid-edit, and — because
 * the `company-id` request header was re-read from that slot at send time — a tab could send a
 * mutation for the company *another* tab had just switched to, writing one customer's data into
 * another customer's database.
 *
 * `sessionStorage` is scoped to the tab: it survives reloads and the Auth0 login round trip, is
 * cloned (not shared) into tabs opened from this one, and dies with the tab. Nothing needs to be
 * kept in sync between tabs, so no cross-tab sync exists any more — that's the point.
 *
 * Consequence worth knowing: masquerade is no longer sticky across browser sessions. Closing the
 * tab and reopening the app lands you in your own account, and you re-enter from the admin app.
 */
function readStored(): string | null {
  try {
    return window?.sessionStorage?.getItem(MASQUERADE_COMPANY_ID_KEY) || null
  } catch {
    return null
  }
}

function writeStored(companyId: string): void {
  try {
    window?.sessionStorage?.setItem(MASQUERADE_COMPANY_ID_KEY, companyId)
  } catch {
    // sessionStorage unavailable (private mode / storage disabled) — callers still get the right
    // value for this render; it just won't survive a reload.
  }
}

/**
 * Adopts a masquerade id left in `localStorage` by a build that predates the per-tab switch, so an
 * in-flight support session isn't dropped by a deploy, then removes the legacy key for good.
 *
 * Only the first tab to load after the deploy can adopt a value, and only when it has no per-tab
 * value of its own — after that the legacy key is gone and this is a no-op forever. Safe to delete
 * once every session predating the switch has expired.
 */
export function migrateLegacyMasquerade(): void {
  try {
    const legacy = window?.localStorage?.getItem(MASQUERADE_COMPANY_ID_KEY)
    if (!legacy) {
      return
    }

    window.localStorage.removeItem(MASQUERADE_COMPANY_ID_KEY)

    // a literal 'null' string got written by older code paths — don't adopt it as a company id
    if (legacy !== 'null' && !readStored()) {
      writeStored(legacy)
    }
  } catch {
    // storage unavailable — nothing to migrate
  }
}

/**
 * Read the company id this tab is masquerading as, if any.
 *
 * Superadmin/support/corporate users can masquerade as another company. Returns `null` when this
 * tab isn't masquerading or when sessionStorage is unavailable (e.g. SSR, privacy mode).
 */
export function getMasqueradeCompanyId(): string | null {
  const stored = readStored()
  // a literal 'null' string gets written by older code paths; treat it as "not masquerading" so
  // every consumer agrees rather than sending `company-id: null` to the API
  return stored === 'null' ? null : stored
}

/**
 * Start (or switch) masquerading in **this tab only**. Every UI that enters an account goes through
 * here — the admin app's Masquerade button (via the URL param), the corporate location switcher, and
 * the location tables/map.
 */
export function setMasqueradeCompanyId(companyId: string): void {
  if (!companyId || companyId === 'null') {
    return
  }
  writeStored(companyId)
  announceChange()
}

/**
 * Stop masquerading in this tab. Other tabs keep whatever account they're in.
 */
export function clearMasqueradeCompanyId(): void {
  try {
    window?.sessionStorage?.removeItem(MASQUERADE_COMPANY_ID_KEY)
  } catch {
    // storage unavailable — nothing stored to clear
  }
  announceChange()
}

/**
 * Resolves which company this tab should act as.
 *
 * An explicit `?masqueradeCompanyId=` wins over the stored value and is persisted for this tab, so
 * later navigations that drop the param stay on the same company. The param is how the admin app
 * hands a company over to a fresh tab, and it's the only cross-app carrier — nothing is inherited
 * from other tabs.
 */
export function resolveMasqueradeCompanyId({
  canMasquerade,
  search
}: {
  canMasquerade: boolean
  search?: string
}): string | null {
  if (!canMasquerade) {
    return null
  }

  migrateLegacyMasquerade()

  const paramId = new URLSearchParams(search ?? '').get(MASQUERADE_COMPANY_ID_KEY)
  if (paramId && paramId !== 'null') {
    if (readStored() !== paramId) {
      writeStored(paramId)
    }
    return paramId
  }

  return getMasqueradeCompanyId()
}

/**
 * Appends this tab's masquerade company id to a same-app href that will open in a new browsing
 * context without an opener (e.g. `target="_blank" rel="noreferrer"`) — sessionStorage isn't
 * cloned into such a tab, so without this it silently loads as the viewer's own account (#22703).
 *
 * No-ops (returns `href` unchanged) when this tab isn't masquerading, when `href` already carries
 * the param, or when `href` isn't a same-app relative path — an absolute/external URL would leak
 * the company id to a third-party host via its own query string or Referer header.
 *
 * Checks for an existing param by parsing the query string with `URLSearchParams` rather than a
 * raw substring match, so a param whose *value* happens to contain the literal text
 * `masqueradeCompanyId=` can't cause a false-positive no-op.
 */
export function withMasqueradeParam(href: string): string {
  const companyId = getMasqueradeCompanyId()
  if (!companyId || !/^\/(?!\/)/.test(href)) {
    return href
  }
  const queryStart = href.indexOf('?')
  const existingQuery = queryStart === -1 ? '' : href.slice(queryStart + 1)
  if (new URLSearchParams(existingQuery).has(MASQUERADE_COMPANY_ID_KEY)) {
    return href
  }
  const separator = queryStart === -1 ? '?' : '&'
  return `${href}${separator}${MASQUERADE_COMPANY_ID_KEY}=${encodeURIComponent(companyId)}`
}

/**
 * Build the `company-id` header used to forward masquerade context to the API.
 *
 * Returns `{ 'company-id': <id> }` when masquerading, otherwise an empty object so it can be
 * spread into request headers unconditionally. The API uses this header to scope a request to
 * the masqueraded company instead of the logged-in user's own company.
 *
 * Prefer passing an explicit id where one is available — the Apollo client binds its own company id
 * at creation (see `createApolloClient`) so it can't drift mid-session. This storage-backed form is
 * for callers that sit above the point where masquerade is resolved, e.g. `ApiClientProvider`.
 */
export function getMasqueradeHeaders(masqueradeCompanyId?: string | null): Record<string, string> {
  const companyId = masqueradeCompanyId === undefined ? getMasqueradeCompanyId() : masqueradeCompanyId
  return companyId && companyId !== 'null' ? { 'company-id': companyId } : {}
}

/**
 * Identity for anything that caches company-scoped data (the Apollo IndexedDB store, the
 * ApolloProvider remount key).
 *
 * `appDocs` takes no company argument and `$options`/`$rates` normalize to company-agnostic cache
 * ids, so the masqueraded company has to be baked into the cache identity itself — otherwise two
 * companies share the same cache slots and one franchise's settings can be saved over another's
 * (ENG-1061).
 *
 * Returns a bare `userId` when not masquerading, so existing offline caches keep their store name
 * and normal users aren't forced into a cold rehydrate.
 */
export function getCacheScopeKey(userId: string, masqueradeCompanyId?: string | null): string {
  const companyId = masqueradeCompanyId === undefined ? getMasqueradeCompanyId() : masqueradeCompanyId
  return companyId ? `${userId}:${companyId}` : userId
}
